import { useCallback, useEffect, useRef, useState } from "react";
import { can, type SessionUser } from "../../shared/auth/access";
import { operationalShiftAt } from "../../../shared/production/plan-window";
import { ApiError } from "../../shared/api/client";
import { shouldNotifyProductionLoadFailure } from "./operator-connection";
import {
  AuthoritativeRunsResponseOwnership,
  AuxiliaryResponseOwnership,
  canApplyConfirmedDowntime,
  canCommitFullLoadState,
  canStartAuthoritativeRunsRefresh,
  replaceConfirmedRun,
} from "./authoritative-runs-refresh";
import type {
  LineReportItem,
  OperatorPerformanceItem,
  ProductionOperationalNotification,
  ProductionDowntime,
  ProductionReferenceData,
  ProductionRun,
} from "./models";
import { productionGateway } from "./production-gateway";
import { ProductionDataRefreshCoordinator, resolveRunsBeforeReference } from "./production-data-refresh";
import { applyConfirmedDowntime as mergeConfirmedDowntime } from "./downtime-reason-queue";
import { loadProductionWorkspace, showProductionLoading } from "./production-workspace-load";
import { ProductionRunsReadCoordinator } from "./production-runs-read";
import { terminalSummaryRuns } from "./operator-operational-summary";

type Notify = (message: string, tone?: "success" | "error") => void;

const operationalDateToday = (now = new Date()) => operationalShiftAt(now.getTime()).date;

/**
 * Keeps the production ledger on its own fast path. Reference data is part of
 * first-screen readiness, but reports and notifications are supplementary and
 * must never keep an operator on the loading screen.
 */
export function useProductionData(
  user: SessionUser,
  notify: Notify,
  dataRefreshVersion: number,
  terminalCode?: string,
  operationalDate?: string,
  terminalSessionId?: string,
) {
  const [reference, setReference] = useState<ProductionReferenceData | null>(null);
  const [runs, setRuns] = useState<ProductionRun[]>([]);
  const [report, setReport] = useState<LineReportItem[]>([]);
  const [operatorReport, setOperatorReport] = useState<OperatorPerformanceItem[]>([]);
  const [notifications, setNotifications] = useState<ProductionOperationalNotification[]>([]);
  const [date, setDate] = useState(operationalDate ?? operationalDateToday());
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loadErrorCode, setLoadErrorCode] = useState<string | null>(null);
  const scopeKey = `${user.id}:${user.role}:${terminalCode ?? ""}:${terminalSessionId ?? ""}:${date}`;
  const readyScopeRef = useRef<string | null>(null);
  const failedScopeRef = useRef<string | null>(null);
  const latestRequestRef = useRef(0);
  const currentScopeRef = useRef(scopeKey);
  currentScopeRef.current = scopeKey;
  const refreshCoordinatorRef = useRef(new ProductionDataRefreshCoordinator());
  const runsReadRef = useRef(new ProductionRunsReadCoordinator());
  const authoritativeRunsOwnershipRef = useRef(new AuthoritativeRunsResponseOwnership());
  const auxiliaryOwnershipRef = useRef(new AuxiliaryResponseOwnership());
  const canSynchronizePlan = user.role !== "VARDIYA_AMIRI" && can(user, "production.run.manage");
  const canReadReport = can(user, "production.report.read");
  const readRuns = useCallback(() => runsReadRef.current.read(scopeKey, () => {
    if (terminalCode && user.role === "URETIM_OPERATORU") {
      return productionGateway.terminalOperationalSummary(terminalCode, date).then(terminalSummaryRuns);
    }
    return user.role === "VARDIYA_AMIRI" && !terminalCode
      ? productionGateway.supervisorRuns(date)
      : productionGateway.runs(date, terminalCode);
  }), [date, scopeKey, terminalCode, user.role]);

  const launchAuxiliaryLoad = useCallback((scope: string, loadAuxiliary: boolean) => {
    if (!loadAuxiliary || !refreshCoordinatorRef.current.startAuxiliary(scope)) return;
    const auxiliaryTicket = auxiliaryOwnershipRef.current.start(scope);
    const result = Promise.allSettled([
      canReadReport
        ? productionGateway.lineReport(date, date).then((value) => value.items)
        : Promise.resolve(null),
      user.role === "URETIM_YONETICISI"
        ? productionGateway.operatorReport(date, date).then((value) => value.items)
        : Promise.resolve(null),
      productionGateway.notifications(terminalCode),
    ]).then(([reportResult, operatorReportResult, notificationsResult]) => {
      const value = <T>(result: PromiseSettledResult<T>): T | null =>
        result.status === "fulfilled" ? result.value : null;
      return {
        report: value(reportResult),
        operatorReport: value(operatorReportResult),
        notifications: value(notificationsResult),
        complete: reportResult.status === "fulfilled" &&
          operatorReportResult.status === "fulfilled" &&
          notificationsResult.status === "fulfilled",
      };
    });
    void result.then((next) => {
      // Keep successful reports/notifications even if one sibling endpoint
      // timed out. A partial auxiliary response must not be invisible merely
      // because another non-critical endpoint failed.
      const ownsTicket = auxiliaryOwnershipRef.current.owns(auxiliaryTicket) && currentScopeRef.current === scope;
      refreshCoordinatorRef.current.finishAuxiliary(scope, ownsTicket && next.complete, Date.now());
      if (!ownsTicket) return;
      if (next.report) setReport(next.report);
      if (next.operatorReport) setOperatorReport(next.operatorReport);
      if (next.notifications) {
        setNotifications(next.notifications);
        const unread = next.notifications.filter((item) => !item.viewedAt);
        if (unread.length) {
          void Promise.allSettled(unread.map((item) => productionGateway.viewNotification(item.id, terminalCode)));
        }
      }
    });
  }, [canReadReport, date, terminalCode, user.role]);

  const load = useCallback(async (silent = false, forceReference = true) => {
    const refresh = refreshCoordinatorRef.current.start({
      scope: scopeKey,
      operator: user.role === "URETIM_OPERATORU",
      background: silent && !forceReference,
      forceReference,
      now: Date.now(),
    });
    if (!refresh) return;
    if (forceReference) runsReadRef.current.invalidate(scopeKey);

    const requestId = ++latestRequestRef.current;
    const blocking = readyScopeRef.current !== scopeKey;
    const runTicket = authoritativeRunsOwnershipRef.current.start(scopeKey);
    const ownsCurrentFullLoad = () => canCommitFullLoadState({
      latestRequest: requestId === latestRequestRef.current,
      currentScope: currentScopeRef.current === scopeKey,
      ownsRunLedger: authoritativeRunsOwnershipRef.current.owns(runTicket),
    });

    if (blocking) {
      // A terminal/session/date scope must not render another scope's work if
      // its first read fails. Same-scope reconnects keep the last good view.
      setLoading(showProductionLoading({ ready: false, failed: failedScopeRef.current === scopeKey, silent }));
      setRuns([]);
      setReference(null);
      setReport([]);
      setOperatorReport([]);
      setNotifications([]);
    }

    let referenceLoaded = false;
    let runsLoaded = false;
    try {
      const nextRuns = readRuns();
      // A superseded response may warm the coordinator without being committed
      // to this workspace. A first read always needs its own reference payload.
      const nextReference = blocking || refresh.loadReference
        ? productionGateway.referenceData(date, terminalCode, user.role === "VARDIYA_AMIRI" && !terminalCode)
        : Promise.resolve(null);
      // The reference payload may include slower ERP-backed metadata. The run
      // ledger is the operator's stop/start truth and must not wait behind it.
      // Observe both promises now so a fast failure is never unhandled.
      void nextRuns.catch(() => undefined);
      void nextReference.catch(() => undefined);
      const { reference: loadedReference } = await resolveRunsBeforeReference(nextRuns, nextReference, (loadedRuns) => {
        runsLoaded = true;
        if (!ownsCurrentFullLoad()) return;
        setRuns(loadedRuns);
        setUpdatedAt(Date.now());
        // A same-scope background refresh has a verified screen already. Keep
        // it connected while the supplementary reference payload arrives.
        if (!blocking) {
          setLoadError(false);
          setLoadErrorCode(null);
        }
      });
      referenceLoaded = loadedReference !== null;
      if (!ownsCurrentFullLoad()) return;

      if (loadedReference) setReference(loadedReference);
      readyScopeRef.current = scopeKey;
      failedScopeRef.current = null;
      setUpdatedAt(Date.now());
      setLoadError(false);
      setLoadErrorCode(null);
      launchAuxiliaryLoad(scopeKey, refresh.loadAuxiliary);
    } catch (error) {
      if (!ownsCurrentFullLoad()) return;
      // A reference refresh failure must not turn a just-confirmed operator
      // ledger into an offline screen. The retained reference remains usable
      // and the next normal poll retries it.
      if (runsLoaded && !blocking) return;
      setLoadError(true);
      failedScopeRef.current = scopeKey;
      const errorCode = error instanceof ApiError ? error.code : null;
      setLoadErrorCode(errorCode);
      if (shouldNotifyProductionLoadFailure({ role: user.role, terminalCode, silent, blocking, errorCode })) {
        notify(error instanceof Error ? error.message : "Üretim alanı yüklenemedi.");
      }
    } finally {
      refreshCoordinatorRef.current.finish(refresh, referenceLoaded, Date.now());
      if (requestId === latestRequestRef.current && blocking) setLoading(false);
    }
  }, [date, launchAuxiliaryLoad, notify, readRuns, scopeKey, terminalCode, user.role]);

  const refreshAuthoritativeRuns = useCallback(async (afterWrite = false) => {
    if (!canStartAuthoritativeRunsRefresh(readyScopeRef.current, scopeKey)) return;
    // The owner of an existing read already applies its response. A joined
    // priority refresh must not steal a full poll's connection/error ownership.
    if (!afterWrite && runsReadRef.current.hasPending(scopeKey)) return;
    if (afterWrite) runsReadRef.current.invalidate(scopeKey);
    const responseTicket = authoritativeRunsOwnershipRef.current.start(scopeKey);
    try {
      const nextRuns = await readRuns();
      if (
        authoritativeRunsOwnershipRef.current.owns(responseTicket) &&
        currentScopeRef.current === scopeKey
      ) {
        setRuns(nextRuns);
        readyScopeRef.current = scopeKey;
        setUpdatedAt(Date.now());
        setLoadError(false);
        setLoadErrorCode(null);
      }
    } catch {
      // Full refreshes own connection UI; a priority read never replaces a
      // verified screen with a false offline state.
    }
  }, [readRuns, scopeKey]);

  const applyConfirmedRun = useCallback((confirmedRun: ProductionRun) => {
    // The mutation response is authoritative only for the active workspace.
    // Do not let a completed request from a changed terminal/date scope alter
    // the new screen.
    if (!canApplyConfirmedDowntime(readyScopeRef.current, currentScopeRef.current, scopeKey)) return;
    // A full read that began before the mutation may otherwise commit its
    // older ledger after this confirmed response.
    authoritativeRunsOwnershipRef.current.invalidate();
    runsReadRef.current.invalidate(scopeKey);
    setRuns((current) => replaceConfirmedRun(current, confirmedRun, Boolean(terminalCode && user.role === "URETIM_OPERATORU")));
    setUpdatedAt(Date.now());
  }, [scopeKey, terminalCode, user.role]);

  const applyConfirmedDowntime = useCallback((runId: string, confirmedDowntime: ProductionDowntime) => {
    if (currentScopeRef.current !== scopeKey || readyScopeRef.current !== scopeKey) return;
    // A prior list request can otherwise restore an older open/closed state
    // after the server has already acknowledged this mutation.
    authoritativeRunsOwnershipRef.current.invalidate();
    runsReadRef.current.invalidate(scopeKey);
    setRuns((current) => current.map((run) => run.id === runId ? mergeConfirmedDowntime(run, confirmedDowntime) : run));
    setUpdatedAt(Date.now());
  }, [scopeKey]);

  useEffect(() => {
    let active = true;
    let first = true;
    void loadProductionWorkspace({
      read: () => { const silent = !first; first = false; return load(silent); },
      synchronize: !terminalCode && canSynchronizePlan ? () => productionGateway.syncPlanFlow(date) : undefined,
      isCurrent: () => active && currentScopeRef.current === scopeKey,
      onSynchronizationFailure: (error) => notify(error instanceof Error ? error.message : "Yeni plan uygulanamadı; mevcut işler gösteriliyor."),
    });
    return () => {
      active = false;
      latestRequestRef.current += 1;
      authoritativeRunsOwnershipRef.current.invalidate();
      runsReadRef.current.invalidate(scopeKey);
      auxiliaryOwnershipRef.current.invalidate();
    };
  }, [canSynchronizePlan, dataRefreshVersion, date, load, notify, scopeKey, terminalCode]);

  useEffect(() => {
    if (operationalDate && operationalDate !== date) setDate(operationalDate);
  }, [date, operationalDate]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const activeDate = terminalCode && operationalDate ? operationalDate : operationalDateToday();
      if (activeDate !== date) setDate(activeDate);
      else if (document.visibilityState === "visible") void load(true, false);
    }, user.role === "URETIM_OPERATORU" ? 5_000 : 15_000);
    const refreshOnFocus = () => {
      if (document.visibilityState === "visible") void load(true, false);
    };
    window.addEventListener("focus", refreshOnFocus);
    window.addEventListener("online", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshOnFocus);
      window.removeEventListener("online", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [date, load, operationalDate, terminalCode, user.role]);

  return {
    reference,
    runs,
    report,
    operatorReport,
    notifications,
    date,
    setDate,
    loading,
    updatedAt,
    loadError,
    loadErrorCode,
    load,
    refreshAuthoritativeRuns,
    applyConfirmedRun,
    applyConfirmedDowntime,
  };
}
